/**
 * Sentry browser-side init.
 *
 * Next.js 16 picks this up automatically (`src/instrumentation-client.ts`)
 * and runs it once on the client. We deliberately do nothing server-side
 * because this app is fully static-exported.
 *
 * The SDK (~200 KB of client JS) is dynamic-imported on idle instead of
 * statically — Sentry has no business inside the LCP window on a static
 * marketing site. Errors thrown in the first seconds before init are the
 * accepted trade-off (task-28 mobile LCP budget).
 *
 * Disabled outside production, and disabled when the DSN env var is missing
 * — so previews / forks without secrets won't try to phone home.
 */

const dsn = process.env.NEXT_PUBLIC_SENTRY_DSN;
const enabled = process.env.NODE_ENV === 'production' && !!dsn;

if (enabled && typeof window !== 'undefined') {
  const start = () => {
    import('@sentry/nextjs').then((Sentry) => {
      Sentry.init({
        dsn,
        enabled: true,

        // Light sampling — agency site, not a transactional app.
        tracesSampleRate: 0.1,

        // No session replay by default; only capture on error.
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 1.0,

        // Filter common noise.
        ignoreErrors: [
          'ResizeObserver loop limit exceeded',
          'ResizeObserver loop completed with undelivered notifications',
          // Browser-extension noise
          'Non-Error promise rejection captured',
          'Script error.',
          'Network request failed',
        ],
        denyUrls: [
          // Browser extensions
          /chrome-extension:\/\//i,
          /moz-extension:\/\//i,
          /safari-extension:\/\//i,
          // Common third-party noise
          /extensions\//i,
          /^chrome:\/\//i,
        ],

        // Tag releases by commit SHA when CI provides it.
        release: process.env.NEXT_PUBLIC_SENTRY_RELEASE,
      });
    });
  };

  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(start, { timeout: 5000 });
  } else {
    setTimeout(start, 3000);
  }
}
